import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

const client = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// 请求拦截：自动附加 JWT token
client.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 网络层重试：QA 2026-04-29 ISSUE-006/008
// prod 侧 ERR_CONNECTION_CLOSED / SSL handshake 抖动会让 axios 报"无 response"
// (error.code 'ERR_NETWORK' / 'ECONNABORTED')。GET 天然幂等，自动重试一次能挡掉
// 大部分一次性抖动；非 GET 请求只在调用方显式标 `config.idempotent = true` 时才重试，
// 避免重复 POST/PATCH 写入。重试只做一次，间隔 600ms，足够覆盖单次握手失败但
// 不会拖长用户感知。
client.interceptors.response.use(
  (response) => response,
  (error) => {
    const cfg = error.config;
    if (!cfg || cfg._netRetried) return Promise.reject(error);
    const noResponse = !error.response;
    const isRetriableMethod =
      (cfg.method || 'get').toLowerCase() === 'get' || cfg.idempotent === true;
    if (noResponse && isRetriableMethod) {
      cfg._netRetried = true;
      return new Promise((resolve) => setTimeout(resolve, 600))
        .then(() => client(cfg));
    }
    return Promise.reject(error);
  }
);

// 响应拦截：token 过期时自动刷新
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // 只有"带过 token 的请求收到 401"才走 token 过期处理 / 跳 /login。
      // 没带 Authorization 的 401 = 业务层 auth 失败(登录密码错、未登录
      // 调受保护接口等)，由调用方自行处理(LoginPage 显示密码错误 etc.)，
      // 不能抢在他们 setError 之前 reload。
      const hadToken = !!originalRequest.headers?.Authorization;
      if (!hadToken) {
        return Promise.reject(error);
      }

      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const { data } = await axios.post(`${BASE_URL}/auth/token/refresh/`, {
            refresh: refreshToken,
          });
          localStorage.setItem('access_token', data.access);
          originalRequest.headers.Authorization = `Bearer ${data.access}`;
          return client(originalRequest);
        } catch {
          // refresh 也失败，清除登录态
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
      } else {
        // 带过 token 但本地没 refresh_token: token 失效又无法 recover。
        // 清残留 + 跳 /login，跟"refresh 失败"统一 ——
        // useProfileGuard 'blocked_auth' 分支依赖这里接管跳转。
        localStorage.removeItem('access_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// 响应拦截：5xx 非 JSON 兜底
// Why: 后端虽已在 base.py 挂 EXCEPTION_HANDLER 把 DRF 视图异常都转成 JSON,
// 但 nginx / gunicorn 自身的 502/503/504、或 Django middleware 早于 DRF dispatch
// 抛出的异常,仍会回 HTML(or text/plain)。前端任何页面 catch 中如果直接
// Object.entries(err.response.data) 都会再现 2026-04-27 注册事故的字符流崩坏。
// 这里统一改写成 { detail: '...' } 标准结构,各页面 catch 只处理标准对象即可。
//
// 注:responseType=blob 的请求(如 ReportPage 的 PDF 下载)由调用方自己解析
// Blob 错误体,这里跳过不动;4xx 不动(DRF ValidationError 是字段对象,不能吞)。
client.interceptors.response.use(
  (response) => response,
  (error) => {
    const resp = error.response;
    if (
      resp &&
      resp.status >= 500 &&
      error.config?.responseType !== 'blob'
    ) {
      const d = resp.data;
      const isPlainObject =
        d &&
        typeof d === 'object' &&
        !Array.isArray(d) &&
        !(typeof Blob !== 'undefined' && d instanceof Blob);
      if (!isPlainObject) {
        // 保留原始 body 到 _raw 方便排查;前端不要直接展示
        error.response = {
          ...resp,
          data: {
            detail:
              resp.status === 502 || resp.status === 503 || resp.status === 504
                ? '服务暂时不可用，请稍后重试'
                : '服务器繁忙，请稍后重试',
            _raw: typeof d === 'string' ? d.slice(0, 500) : null,
          },
        };
      }
    }
    return Promise.reject(error);
  }
);

export default client;
